import { html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LandiStorefrontElement, readEmbedThemeFromWindow } from '../base/landi-storefront-element.js';
import { sharedStyles, buttonStyles, badgeStyles } from '../styles/shared.js';
import {
  formatPrice,
  cartState,
  storeApi,
  resolveProductImageUrl,
  getProductImageDisplay,
  sanitizeCustomerFacingCopy,
  type Product,
  type ProductVariant,
} from '@landi/store-core';
import './landi-product-card.js';
import './landi-product-image.js';

/** Variant axes for multi-dimensional SKU options (form, size, origin). */
type VariantAxis = 'form' | 'size' | 'origin' | 'line';

const VARIANT_AXIS_ORDER: VariantAxis[] = ['form', 'size', 'origin', 'line'];

const VARIANT_AXIS_LABELS: Record<VariantAxis, string> = {
  form: 'Form',
  size: 'Size',
  origin: 'Origin',
  line: 'Line',
};

const VARIANT_OPTION_LABELS: Record<string, string> = {
  lyophilized: 'Lyophilized',
  'non-lyophilized': 'Non-Lyophilized',
  capsule: 'Capsule',
  powder: 'Powder',
  spray: 'Spray',
  CHN: 'China',
  US: 'USA',
};

/**
 * Full product detail page component
 * Matches The Golden Peptides product page design
 * 
 * @element landi-product-detail
 * @fires add-to-cart - Fired when product is added to cart
 */
@customElement('landi-product-detail')
export class LandiProductDetail extends LandiStorefrontElement {
  static styles = [
    sharedStyles,
    buttonStyles,
    badgeStyles,
    css`
      :host {
        display: block;
        background: var(--landi-background);
        min-height: 100vh;
      }

      :host([minimal]) {
        min-height: auto;
        background: transparent;
      }

      :host([minimal]) .container {
        max-width: 720px;
        padding: 1rem;
      }

      :host([minimal]) .product-grid {
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
        margin-bottom: 0;
      }

      @media (max-width: 700px) {
        :host([minimal]) .product-grid {
          grid-template-columns: 1fr;
        }
      }
      
      .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 2rem 1rem;
      }
      
      .breadcrumb {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        margin-bottom: 2rem;
      }
      
      .breadcrumb a {
        color: var(--landi-text-muted);
        text-decoration: none;
      }
      
      .breadcrumb a:hover {
        color: var(--landi-primary);
      }
      
      .breadcrumb-separator {
        color: var(--landi-border);
      }
      
      .product-grid {
        display: grid;
        grid-template-columns: 460px 1fr;
        gap: 4rem;
        margin-bottom: 4rem;
        align-items: flex-start;
      }
      
      @media (max-width: 900px) {
        .product-grid {
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
      }
      
      .product-image-section {
        position: sticky;
        top: 2rem;
      }
      
      .main-image {
        width: 100%;
        aspect-ratio: 1;
        object-fit: cover;
        border-radius: var(--landi-radius-lg);
        background: var(--landi-surface);
      }

      .gallery-thumbnails {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.75rem;
      }

      .gallery-thumb {
        width: 56px;
        height: 56px;
        padding: 0;
        overflow: hidden;
        border-radius: var(--landi-radius);
        border: 2px solid var(--landi-border);
        cursor: pointer;
        background: var(--landi-surface);
        transition: border-color var(--landi-transition);
      }

      .gallery-thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .gallery-thumb:hover {
        border-color: var(--landi-primary);
      }

      .gallery-thumb.selected {
        border-color: var(--landi-primary);
        box-shadow: 0 0 0 1px var(--landi-primary);
      }
      
      .product-info {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }
      
      .product-header {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .price-size-qty-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 1rem;
        margin-top: 0.5rem;
      }

      .price-size-qty-row .product-price {
        margin: 0;
      }

      .info-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 1rem 1.5rem;
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        margin-top: 0.25rem;
      }

      .info-row span {
        display: inline;
      }

      .info-row .info-value {
        font-weight: 600;
        color: var(--landi-text);
      }

      .info-row .info-value.success {
        color: var(--landi-success);
      }
      
      .product-title {
        font-size: 2rem;
        font-weight: 700;
        margin: 0;
        color: var(--landi-text);
      }
      
      .product-rating {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-size: 0.875rem;
      }
      
      .stars {
        color: var(--landi-primary);
        font-size: 1rem;
      }
      
      .rating-text {
        color: var(--landi-text-muted);
      }
      
      .product-price {
        font-size: 2rem;
        font-weight: 700;
        color: var(--landi-primary);
      }
      
      .section-title {
        font-size: 1rem;
        font-weight: 600;
        margin: 0 0 0.75rem;
        color: var(--landi-text);
      }
      
      .description-text {
        color: var(--landi-text-muted);
        line-height: 1.6;
        margin: 0;
      }
      
      .specs-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      
      .specs-list li {
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .specs-list li::before {
        content: '•';
        color: var(--landi-primary);
      }
      
      .variant-pickers {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 100%;
      }

      .variant-axis-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .variant-axis-label {
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--landi-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin: 0;
      }

      .variant-selector {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
      }
      
      .variant-btn {
        padding: 0.75rem 1.25rem;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        background: var(--landi-surface);
        color: var(--landi-text);
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all var(--landi-transition);
      }
      
      .variant-btn:hover:not(:disabled) {
        border-color: var(--landi-primary);
      }
      
      .variant-btn.selected {
        border-color: var(--landi-primary);
        background: rgba(212, 168, 71, 0.1);
        color: var(--landi-primary);
      }
      
      .variant-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .quantity-section {
        display: flex;
        align-items: center;
        gap: 1rem;
      }
      
      .quantity-controls {
        display: flex;
        align-items: center;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        overflow: hidden;
      }
      
      .qty-btn {
        width: 40px;
        height: 40px;
        border: none;
        background: var(--landi-surface);
        color: var(--landi-text);
        font-size: 1.25rem;
        cursor: pointer;
        transition: background var(--landi-transition);
      }
      
      .qty-btn:hover:not(:disabled) {
        background: var(--landi-surface-elevated);
      }
      
      .qty-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .qty-value {
        width: 60px;
        text-align: center;
        font-size: 1rem;
        font-weight: 500;
        background: var(--landi-surface);
        border: none;
        color: var(--landi-text);
      }
      
      .actions {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      
      .btn-add-cart {
        padding: 1rem 2rem;
        font-size: 1rem;
      }
      
      .wholesale-link {
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        text-align: center;
        cursor: pointer;
      }
      
      .wholesale-link:hover {
        color: var(--landi-primary);
      }
      
      .compliance-note {
        display: inline-flex;
        align-items: center;
        padding: 0.35rem 0.875rem;
        background: var(--landi-surface-elevated, #f3f4f6);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-full, 9999px);
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-weight: 600;
        color: var(--landi-text-muted);
        margin-top: 0.25rem;
        margin-bottom: 0.5rem;
        align-self: flex-start;
      }

      .product-description {
        margin-top: 0.5rem;
        margin-bottom: 0;
      }
      
      .details-layout {
        display: grid;
        grid-template-columns: 1fr 400px;
        gap: 4rem;
        margin-top: 3rem;
        padding-top: 3rem;
        border-top: 1px solid var(--landi-border);
        align-items: flex-start;
      }
      
      @media (max-width: 900px) {
        .details-layout {
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
      }
      
      .content-column {
        display: flex;
        flex-direction: column;
        gap: 3rem;
      }
      
      .content-section {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      
      .content-section h3 {
        font-family: 'Playfair Display', serif;
        font-size: 1.5rem;
        margin: 0;
        color: var(--landi-text);
      }
      
      .content-section p {
        font-size: 1rem;
        line-height: 1.8;
        color: var(--landi-text-muted);
        margin: 0;
      }
      
      .safety-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      
      .safety-list li {
        position: relative;
        padding-left: 1.5rem;
        font-size: 0.9375rem;
        line-height: 1.6;
        color: var(--landi-text-muted);
      }
      
      .safety-list li::before {
        content: '•';
        position: absolute;
        left: 0;
        color: var(--landi-text);
        font-weight: bold;
      }
      
      .specs-card {
        background: var(--landi-surface, #ffffff);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-lg, 12px);
        padding: 2rem;
        box-shadow: 0 10px 30px rgba(0,0,0,0.04);
        position: sticky;
        top: 6rem;
      }
      
      .specs-card h3 {
        font-family: 'Playfair Display', serif;
        font-size: 1.25rem;
        margin: 0 0 1.5rem 0;
        color: var(--landi-text);
        border-bottom: 2px solid var(--landi-background);
        padding-bottom: 1rem;
      }
      
      .spec-item {
        display: flex;
        justify-content: space-between;
        padding: 0.875rem 0;
        border-bottom: 1px solid var(--landi-border);
        font-size: 0.875rem;
        align-items: center;
      }
      
      .spec-item:last-of-type {
        border-bottom: none;
      }
      
      .spec-label {
        font-weight: 500;
        color: var(--landi-text-muted);
      }
      
      .spec-value {
        font-weight: 600;
        color: var(--landi-text);
        text-align: right;
        max-width: 65%;
      }
      
      .spec-value.success {
        color: var(--landi-success);
        background: rgba(16, 185, 129, 0.1);
        padding: 0.25rem 0.5rem;
        border-radius: var(--landi-radius);
      }
      
      .specs-actions {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px dashed var(--landi-border);
      }
      
      .related-section {
        margin-top: 4rem;
        border-top: 1px solid var(--landi-border);
        padding-top: 3rem;
      }
      
      .related-title {
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0 0 2rem;
        text-align: center;
      }
      
      .related-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 1.5rem;
      }
      
      @media (max-width: 1024px) {
        .related-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      
      @media (max-width: 640px) {
        .related-grid {
          grid-template-columns: 1fr;
        }
      }
      
      .toast {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        background: var(--landi-success);
        color: white;
        padding: 1rem 1.5rem;
        border-radius: var(--landi-radius);
        box-shadow: var(--landi-shadow);
        transform: translateY(150%);
        transition: transform 0.3s ease;
        z-index: 1000;
      }
      
      .toast.show {
        transform: translateY(0);
      }
      
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        opacity: 0;
        visibility: hidden;
        transition: all 0.2s ease;
      }
      .modal-overlay.open {
        opacity: 1;
        visibility: visible;
      }
      .modal {
        background: var(--landi-surface, #fff);
        padding: 2rem;
        border-radius: var(--landi-radius-lg, 8px);
        width: 100%;
        max-width: 500px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        transform: translateY(20px);
        transition: transform 0.2s ease;
        max-height: 90vh;
        overflow-y: auto;
      }
      .modal-overlay.open .modal {
        transform: translateY(0);
      }
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5rem;
      }
      .modal-title {
        font-size: 1.25rem;
        font-weight: 700;
        margin: 0;
        color: var(--landi-text);
      }
      .modal-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: var(--landi-text-muted);
      }
      .form-group {
        margin-bottom: 1rem;
      }
      .form-group label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        font-size: 0.875rem;
        color: var(--landi-text);
      }
      .form-group input, .form-group textarea {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        font-family: inherit;
        background: var(--landi-background);
        color: var(--landi-text);
        box-sizing: border-box;
      }
    `,
  ];

  @property({ type: String, attribute: 'product-id' }) productId = '';
  @property({ type: String, attribute: 'home-href' }) homeHref = 'index.html';
  @property({ type: String, attribute: 'products-href' }) productsHref = 'index.html#products';
  @property({ type: String, attribute: 'cart-href' }) cartHref = 'cart.html';
  @property({ type: String, attribute: 'checkout-href' }) checkoutHref = 'checkout.html';
  @property({ type: String, attribute: 'related-href-base' }) relatedHrefBase = 'product.html?id=';
  @property({ type: Boolean, reflect: true }) minimal = false;
  /**
   * Hide the in-component RUO compliance banner when the host page already
   * shows its own chrome disclaimer (e.g. biohacks gold site banner).
   * Also honored via `LANDI_STORE_THEME.showRuoBanner === false`.
   */
  @property({ type: Boolean, attribute: 'hide-ruo-banner', reflect: true })
  hideRuoBanner = false;
  @state() private product: Product | null = null;
  @state() private selectedVariant: ProductVariant | null = null;
  @state() private selectedOptions: Record<string, string> = {};
  @state() private quantity = 1;
  @state() private relatedProducts: Product[] = [];
  @state() private showToast = false;
  @state() private showSdsModal = false;
  @state() private showCoaModal = false;
  @state() private sdsFormSubmitted = false;
  @state() private selectedImageIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.syncRuoBannerFromTheme();
    this.syncProductIdFromUrl();
    this.loadProduct();
  }

  /** True when the grey component RUO note should render (default for other stores). */
  private get showRuoBanner(): boolean {
    return !this.hideRuoBanner && !this.minimal;
  }

  private syncRuoBannerFromTheme(): void {
    const theme = readEmbedThemeFromWindow();
    if (!theme || typeof theme !== 'object') {
      return;
    }
    const showRuoBanner = (theme as Record<string, unknown>).showRuoBanner;
    if (showRuoBanner === false) {
      this.hideRuoBanner = true;
    }
  }

  /**
   * Host pages often set `product-id` via an inline module script, which CSP
   * may block. Fall back to `?id=` / `?product=` query params when unset.
   */
  private syncProductIdFromUrl(): void {
    if (this.productId.trim()) {
      return;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('id') ?? params.get('product');
      if (fromQuery && fromQuery.trim()) {
        this.productId = fromQuery.trim();
      }
    } catch {
      // Ignore URL parse failures in non-browser test environments.
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('productId')) {
      this.loadProduct();
    }
  }

  private async loadProduct() {
    if (!this.productId) return;

    const product = await storeApi.getProduct(this.productId);
    if (product) {
      this.product = product;
      this.selectedImageIndex = 0;
      this.initializeVariantSelection(product.variants);
      if (!this.minimal) {
        this.relatedProducts = await storeApi.getRelatedProducts(this.productId, 4);
      } else {
        this.relatedProducts = [];
      }

      // Dispatch product-loaded event for parent page to update title, etc.
      this.dispatchEvent(new CustomEvent('product-loaded', {
        bubbles: true,
        composed: true,
        detail: {
          id: product.id,
          name: product.name,
          price: product.price,
        },
      }));
    }
  }

  private detectVariantAxes(variants: ProductVariant[]): VariantAxis[] {
    return VARIANT_AXIS_ORDER.filter((axis) => {
      const values = new Set(
        variants
          .map((variant) => variant.options[axis])
          .filter((value): value is string => Boolean(value))
      );
      return values.size > 1;
    });
  }

  private getAxisValues(variants: ProductVariant[], axis: VariantAxis): string[] {
    const values = new Set<string>();
    for (const variant of variants) {
      const value = variant.options[axis];
      if (value) {
        values.add(value);
      }
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }

  private resolveVariantFromOptions(
    variants: ProductVariant[],
    options: Record<string, string>,
    axes: VariantAxis[]
  ): ProductVariant | null {
    if (axes.length === 0) {
      return null;
    }
    return (
      variants.find((variant) =>
        axes.every((axis) => {
          const selected = options[axis];
          if (!selected) {
            return true;
          }
          return variant.options[axis] === selected;
        })
      ) ?? null
    );
  }

  private isAxisOptionAvailable(
    variants: ProductVariant[],
    axis: VariantAxis,
    value: string,
    currentSelection: Record<string, string>,
    axes: VariantAxis[]
  ): boolean {
    const otherAxes = axes.filter((candidate) => candidate !== axis);
    return variants.some((variant) => {
      if (variant.options[axis] !== value) {
        return false;
      }
      return otherAxes.every((otherAxis) => {
        const selected = currentSelection[otherAxis];
        if (!selected) {
          return true;
        }
        return variant.options[otherAxis] === selected;
      });
    });
  }

  private formatAxisOptionLabel(value: string): string {
    return VARIANT_OPTION_LABELS[value] ?? value;
  }

  private initializeVariantSelection(variants: ProductVariant[]): void {
    if (variants.length === 0) {
      this.selectedVariant = null;
      this.selectedOptions = {};
      return;
    }

    const preferred = variants.find((variant) => variant.inStock) ?? variants[0];
    if (!preferred) {
      this.selectedVariant = null;
      this.selectedOptions = {};
      return;
    }

    this.selectedVariant = preferred;
    this.selectedOptions = { ...preferred.options };
    this.quantity = 1;
  }

  private selectVariant(variant: ProductVariant): void {
    this.selectedVariant = variant;
    this.selectedOptions = { ...variant.options };
    this.quantity = 1;
  }

  private selectAxisOption(axis: VariantAxis, value: string): void {
    if (!this.product) {
      return;
    }

    const variants = this.product.variants;
    const axes = this.detectVariantAxes(variants);
    const nextSelection = { ...this.selectedOptions, [axis]: value };
    let resolved = this.resolveVariantFromOptions(variants, nextSelection, axes);

    if (!resolved) {
      const fallback = variants.find((variant) => variant.options[axis] === value);
      if (!fallback) {
        return;
      }
      resolved = fallback;
      this.selectedOptions = { ...fallback.options };
    } else {
      this.selectedOptions = nextSelection;
    }

    this.selectedVariant = resolved;
    this.quantity = 1;
  }

  private getMaxQuantity(): number {
    if (this.selectedVariant) {
      return this.selectedVariant.inventoryQuantity;
    }
    return this.product?.inventoryQuantity ?? 99;
  }

  private canAddToCart(): boolean {
    if (!this.product) {
      return false;
    }
    if (this.product.variants.length === 0) {
      return this.product.inventoryQuantity > 0;
    }
    return Boolean(this.selectedVariant?.inStock);
  }

  private updateQuantity(delta: number) {
    const maxQty = this.getMaxQuantity();
    const newQty = this.quantity + delta;
    if (newQty >= 1 && newQty <= maxQty) {
      this.quantity = newQty;
    }
  }

  private addToCart() {
    if (!this.product) {
      return;
    }

    const variant =
      this.product.variants.length > 0 ? this.selectedVariant ?? undefined : undefined;
    if (this.product.variants.length > 0 && !variant) {
      return;
    }

    cartState.addItem(this.product, variant, this.quantity);

    this.dispatchEvent(new CustomEvent('add-to-cart', {
      bubbles: true,
      composed: true,
      detail: {
        productId: this.productId,
        variantId: variant?.id,
        sku: variant?.sku,
        quantity: this.quantity,
      },
    }));

    // Show toast
    this.showToast = true;
    setTimeout(() => {
      this.showToast = false;
    }, 3000);
  }

  private submitSdsRequest(e: Event) {
    e.preventDefault();
    this.sdsFormSubmitted = true;
  }

  private downloadDummyPDF(e: Event, productName: string) {
    e.preventDefault();
    const date = new Date().toLocaleDateString();
    const safeName = productName.replace(/\(/g, '\\(').replace(/\)/g, '\\)');

    // Create a dynamic, multi-line PDF stream with the product metadata
    const streamContent = `BT
/F1 24 Tf
50 720 Td
(Certificate of Analysis) Tj
/F1 12 Tf
0 -40 Td
(Product: ${safeName}) Tj
0 -20 Td
(Date: ${date}) Tj
0 -20 Td
(Purity: 99.4%) Tj
0 -20 Td
(Test Method: HPLC / Mass Spectrometry) Tj
0 -20 Td
(Status: PASS - Meets all laboratory specifications) Tj
0 -40 Td
/F1 10 Tf
(FOR RESEARCH USE ONLY. NOT FOR HUMAN CONSUMPTION.) Tj
ET`;

    const streamLength = streamContent.length;

    const pdfData = `%PDF-1.4
1 0 obj
<< /Type /Catalog /Pages 2 0 R >>
endobj
2 0 obj
<< /Type /Pages /Kids [3 0 R] /Count 1 >>
endobj
3 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>
endobj
4 0 obj
<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>
endobj
5 0 obj
<< /Length ${streamLength} >>
stream
${streamContent}
endstream
endobj
xref
0 6
0000000000 65535 f 
0000000009 00000 n 
0000000058 00000 n 
0000000115 00000 n 
0000000224 00000 n 
0000000312 00000 n 
trailer
<< /Size 6 /Root 1 0 R >>
startxref
405
%%EOF`;

    const blob = new Blob([pdfData], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${productName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_CoA.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    this.showCoaModal = false;
  }

  render() {
    if (!this.product) {
      return html`<div class="container">Loading...</div>`;
    }

    const p = this.product;
    const price = this.selectedVariant?.price ?? p.price;
    const customerDescription = p.description
      ? sanitizeCustomerFacingCopy(p.description)
      : '';
    const variantAxes = this.detectVariantAxes(p.variants);
    const useMultiAxisPickers = variantAxes.length > 0;
    const showFlatVariantPicker = p.variants.length > 1 && !useMultiAxisPickers;
    const stockQuantity = this.selectedVariant?.inventoryQuantity ?? p.inventoryQuantity;
    const inStock = this.selectedVariant
      ? this.selectedVariant.inStock
      : p.inventoryQuantity > 0;

    return html`
      <div class="container">
        ${this.minimal ? '' : html`
        <nav class="breadcrumb" aria-label="Breadcrumb">
          <a href="${this.homeHref}">Home</a>
          <span class="breadcrumb-separator">/</span>
          <a href="${this.productsHref}">${p.category || 'Products'}</a>
          <span class="breadcrumb-separator">/</span>
          <span>${p.name}</span>
        </nav>
        `}
        
        <div class="product-grid">
          <div class="product-image-section">
            ${(() => {
              const images = [...(p.images || [])].sort((a, b) => a.position - b.position);
              const mainImg = images[this.selectedImageIndex] || images[0];
              const mainDisplay = getProductImageDisplay({
                url: resolveProductImageUrl(mainImg?.url),
                name: p.name,
                sku: p.sku,
                category: p.category,
                theme: 'light',
              });
              const mainAlt = mainImg?.altText || p.name;
              return html`
                <img 
                  class="main-image" 
                  src=${mainDisplay.src}
                  alt=${mainAlt}
                  .onerror=${mainDisplay.onError}
                />
                ${images.length > 1 ? html`
                  <div class="gallery-thumbnails" role="tablist" aria-label="Product images">
                    ${images.map((img, i) => {
                      const thumbDisplay = getProductImageDisplay({
                        url: resolveProductImageUrl(img.url),
                        name: p.name,
                        sku: p.sku,
                        category: p.category,
                        theme: 'light',
                      });
                      return html`
                      <button
                        type="button"
                        role="tab"
                        aria-selected=${this.selectedImageIndex === i}
                        aria-label=${img.altText || `Image ${i + 1}`}
                        class="gallery-thumb ${this.selectedImageIndex === i ? 'selected' : ''}"
                        @click=${() => { this.selectedImageIndex = i; }}
                      >
                        <img src=${thumbDisplay.src} alt="" .onerror=${thumbDisplay.onError} />
                      </button>
                    `;
                    })}
                  </div>
                ` : ''}
              `;
            })()}
          </div>
          
          <div class="product-info">
            <div class="product-header">
              <h1 class="product-title">${p.name}</h1>
              ${this.showRuoBanner
                ? html`
              <div class="compliance-note" title="Compliance Notice" role="note">
                <span>FOR RESEARCH USE ONLY. Not for human consumption.</span>
              </div>`
                : ''}
              ${!this.minimal && customerDescription
                ? html`<p class="description-text product-description">${customerDescription}</p>`
                : ''}
              ${!this.minimal && p.metadata?.reviews
                ? html`
                <div class="product-rating">
                  <span class="stars">★★★★★</span>
                  <span class="rating-text">(${p.metadata.reviews} Reviews)</span>
                </div>`
                : ''}
            </div>

            <div class="price-size-qty-row">
              <div class="product-price">${formatPrice(price)}</div>
              ${useMultiAxisPickers ? html`
                <div class="variant-pickers" role="group" aria-label="Product variant options">
                  ${variantAxes.map((axis) => html`
                    <div class="variant-axis-group">
                      <p class="variant-axis-label" id="variant-axis-${axis}">${VARIANT_AXIS_LABELS[axis]}</p>
                      <div class="variant-selector" role="radiogroup" aria-labelledby="variant-axis-${axis}">
                        ${this.getAxisValues(p.variants, axis).map((value) => {
                          const available = this.isAxisOptionAvailable(
                            p.variants,
                            axis,
                            value,
                            this.selectedOptions,
                            variantAxes
                          );
                          const matchingVariant = this.resolveVariantFromOptions(
                            p.variants,
                            { ...this.selectedOptions, [axis]: value },
                            variantAxes
                          );
                          const optionInStock = matchingVariant?.inStock ?? false;
                          const isSelected = this.selectedOptions[axis] === value;
                          return html`
                            <button
                              type="button"
                              role="radio"
                              aria-checked=${isSelected}
                              class="variant-btn ${isSelected ? 'selected' : ''}"
                              ?disabled=${!available}
                              @click=${() => this.selectAxisOption(axis, value)}
                            >
                              ${this.formatAxisOptionLabel(value)}${available && !optionInStock ? ' (Out of Stock)' : ''}
                            </button>
                          `;
                        })}
                      </div>
                    </div>
                  `)}
                </div>
              ` : ''}
              ${showFlatVariantPicker ? html`
                <div class="variant-selector" role="radiogroup" aria-label="Product variants">
                  ${p.variants.map((variant) => html`
                    <button
                      type="button"
                      role="radio"
                      aria-checked=${this.selectedVariant?.id === variant.id}
                      class="variant-btn ${this.selectedVariant?.id === variant.id ? 'selected' : ''}"
                      ?disabled=${!variant.inStock}
                      @click=${() => this.selectVariant(variant)}
                    >
                      ${variant.name}${!variant.inStock ? ' (Out of Stock)' : ''}
                    </button>
                  `)}
                </div>
              ` : ''}
              <div class="quantity-controls">
                <button 
                  class="qty-btn" 
                  @click=${() => this.updateQuantity(-1)}
                  ?disabled=${this.quantity <= 1}
                >−</button>
                <input 
                  class="qty-value" 
                  type="text" 
                  .value=${String(this.quantity)}
                  readonly
                  aria-label="Quantity"
                />
                <button 
                  class="qty-btn" 
                  @click=${() => this.updateQuantity(1)}
                  ?disabled=${this.quantity >= this.getMaxQuantity()}
                >+</button>
              </div>
            </div>

            <div class="info-row">
              <span>Availability: <span class="info-value ${inStock ? 'success' : ''}">${inStock ? `${stockQuantity} in stock` : 'Out of stock'}</span></span>
              ${this.minimal ? '' : html`
              ${this.selectedVariant?.sku ? html`<span>SKU: <span class="info-value">${this.selectedVariant.sku}</span></span>` : ''}
              ${p.metadata?.format ? html`<span>Format: <span class="info-value">${p.metadata.format}</span></span>` : ''}
              ${p.metadata?.purity ? html`<span>Purity: <span class="info-value success">${p.metadata.purity}</span></span>` : ''}
              ${p.metadata?.testMethod ? html`<span>Method: <span class="info-value">${p.metadata.testMethod}</span></span>` : ''}
              `}
            </div>
            
            <div class="actions">
              <button 
                class="btn btn-primary btn-add-cart"
                ?disabled=${!this.canAddToCart()}
                @click=${this.addToCart}
              >
                Add to Cart
              </button>
              ${this.minimal ? '' : html`
              <span class="wholesale-link">
                Ordering in bulk? Click here for wholesale pricing.
              </span>
              `}
            </div>
          </div>
        </div>
        
        ${this.minimal ? '' : html`
        <div class="details-layout">
          <div class="content-column">
            <div class="content-section">
              <h3>Overview</h3>
              <p>${customerDescription}</p>
            </div>
            
            ${p.metadata?.storageConditions || p.metadata?.handlingInstructions || p.metadata?.safetyInfo ? html`
              <div class="content-section">
                <h3>Storage & Handling Protocols</h3>
                <p>Please adhere strictly to the following guidelines to maintain compound integrity and laboratory safety.</p>
                <ul class="safety-list" style="margin-top: 0.5rem;">
                  ${p.metadata.storageConditions ? html`<li><strong>Storage:</strong> ${p.metadata.storageConditions}</li>` : ''}
                  ${p.metadata.handlingInstructions ? html`<li><strong>Handling:</strong> ${p.metadata.handlingInstructions}</li>` : ''}
                  ${p.metadata.safetyInfo ? html`<li><strong>Safety:</strong> ${p.metadata.safetyInfo}</li>` : ''}
                </ul>
              </div>
            ` : ''}
          </div>
          
          <div class="specs-card">
            <h3>Compound Specifications</h3>
            
            ${p.metadata?.purity ? html`
              <div class="spec-item">
                <span class="spec-label">Purity</span>
                <span class="spec-value success">${p.metadata.purity}</span>
              </div>
            ` : ''}
            
            ${p.metadata?.testMethod ? html`
              <div class="spec-item">
                <span class="spec-label">Method</span>
                <span class="spec-value">${p.metadata.testMethod}</span>
              </div>
            ` : ''}
            
            ${p.metadata?.sequence ? html`
              <div class="spec-item">
                <span class="spec-label">Sequence</span>
                <span class="spec-value" style="font-family: monospace; font-size: 0.75rem;">${p.metadata.sequence}</span>
              </div>
            ` : ''}
            
            ${p.metadata?.format ? html`
              <div class="spec-item">
                <span class="spec-label">Format</span>
                <span class="spec-value">${p.metadata.format}</span>
              </div>
            ` : ''}

            ${p.metadata?.batchNumber ? html`
              <div class="spec-item">
                <span class="spec-label">Batch</span>
                <span class="spec-value">#${p.metadata.batchNumber}</span>
              </div>
            ` : ''}
            
            <div class="specs-actions">
              <button class="btn btn-secondary" style="width: 100%; display: flex; justify-content: space-between; align-items: center;" @click=${() => this.showCoaModal = true}>
                <span>View Certificate of Analysis</span>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 16px; height: 16px;"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
              </button>
              
              <button class="btn btn-secondary" style="width: 100%; display: flex; justify-content: space-between; align-items: center;" @click=${() => this.showSdsModal = true}>
                <span>Request SDS Document</span>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 16px; height: 16px;"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
              </button>
            </div>
          </div>
        </div>
        </div>
        `}
        
        ${!this.minimal && this.relatedProducts.length > 0 ? html`
          <section class="related-section">
            <h2 class="related-title">Related Peptides</h2>
            <div class="related-grid">
              ${this.relatedProducts.map(rp => html`
                <landi-product-card
                  product-id=${rp.id}
                  .product=${rp}
                  name=${rp.name}
                  price=${rp.price}
                  .comparePrice=${rp.compareAtPrice}
                  image=${resolveProductImageUrl(rp.images[0]?.url) || ''}
                  sku=${rp.sku || ''}
                  category=${rp.category || ''}
                  description=${rp.shortDescription || ''}
                  .inStock=${rp.inventoryQuantity > 0}
                  format=${rp.metadata?.format || ''}
                  purity=${rp.metadata?.purity || ''}
                  test-method=${rp.metadata?.testMethod || ''}
                  quantity=${rp.variants?.[0]?.name || ''}
                  href="${this.relatedHrefBase}${rp.id}"
                ></landi-product-card>
              `)}
            </div>
          </section>
        ` : ''}
      </div>
      
      <div class="toast ${this.showToast ? 'show' : ''}">
        ✓ Added to cart
      </div>

      ${this.minimal ? '' : html`
      <div class="modal-overlay ${this.showSdsModal ? 'open' : ''}" @click=${(e: Event) => { if (e.target === e.currentTarget) this.showSdsModal = false; }}>
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">Request Safety Data Sheet (SDS)</h3>
            <button class="modal-close" @click=${() => this.showSdsModal = false}>&times;</button>
          </div>
          ${this.sdsFormSubmitted ? html`
            <div style="text-align: center; padding: 2rem 0;">
              <div style="color: var(--landi-success); font-size: 3rem; margin-bottom: 1rem;">✓</div>
              <h4 style="margin-top: 0; color: var(--landi-text);">Request Submitted</h4>
              <p class="description-text">Your SDS request for ${p.name} has been received. Our compliance team will email you the document within 24 hours.</p>
              <button class="btn btn-primary" style="margin-top: 1.5rem;" @click=${() => { this.showSdsModal = false; setTimeout(() => this.sdsFormSubmitted = false, 300); }}>Close</button>
            </div>
          ` : html`
            <p class="description-text" style="margin-bottom: 1.5rem;">Please provide your contact information and research institution to request the Safety Data Sheet for ${p.name}.</p>
            <form @submit=${this.submitSdsRequest}>
              <div class="form-group">
                <label for="sds-name">Full Name <span style="color: var(--landi-error, #ef4444);">*</span></label>
                <input type="text" id="sds-name" required />
              </div>
              <div class="form-group">
                <label for="sds-email">Institutional Email <span style="color: var(--landi-error, #ef4444);">*</span></label>
                <input type="email" id="sds-email" required />
              </div>
              <div class="form-group">
                <label for="sds-institution">Research Institution/Company <span style="color: var(--landi-error, #ef4444);">*</span></label>
                <input type="text" id="sds-institution" required />
              </div>
              <div class="form-group">
                <label for="sds-intent">Intended Research Application <span style="color: var(--landi-error, #ef4444);">*</span></label>
                <textarea id="sds-intent" rows="3" required></textarea>
              </div>
              <div class="form-group" style="display: flex; gap: 0.5rem; align-items: flex-start; margin-top: 1.5rem;">
                <input type="checkbox" id="sds-attest" required style="width: auto; margin-top: 0.25rem;" />
                <label for="sds-attest" style="font-weight: 400; font-size: 0.813rem; color: var(--landi-text-muted);">I verify that I am requesting this document for rigorous laboratory research and agree to abide by all stated safety and handling protocols.</label>
              </div>
              <div style="display: flex; justify-content: flex-end; gap: 1rem; margin-top: 2rem;">
                <button type="button" class="btn btn-secondary" @click=${() => this.showSdsModal = false}>Cancel</button>
                <button type="submit" class="btn btn-primary">Submit Request</button>
              </div>
            </form>
          `}
        </div>
      </div>

      <div class="modal-overlay ${this.showCoaModal ? 'open' : ''}" @click=${(e: Event) => { if (e.target === e.currentTarget) this.showCoaModal = false; }}>
        <div class="modal" style="max-width: 600px;">
          <div class="modal-header">
            <h3 class="modal-title">Certificate of Analysis (CoA)</h3>
            <button class="modal-close" @click=${() => this.showCoaModal = false}>&times;</button>
          </div>
          <div style="background: var(--landi-background); padding: 1.5rem; border-radius: var(--landi-radius); border: 1px solid var(--landi-border); margin-bottom: 1.5rem;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; font-size: 0.875rem;">
              <div>
                <p style="margin: 0 0 0.5rem; color: var(--landi-text-muted);">Compound Name</p>
                <p style="margin: 0; font-weight: 600;">${p.name}</p>
              </div>
              <div>
                <p style="margin: 0 0 0.5rem; color: var(--landi-text-muted);">Batch Number</p>
                <p style="margin: 0; font-weight: 600;">#${p.metadata?.batchNumber}</p>
              </div>
              <div>
                <p style="margin: 0 0 0.5rem; color: var(--landi-text-muted);">Testing Method</p>
                <p style="margin: 0; font-weight: 600;">${p.metadata?.testMethod || 'HPLC'}</p>
              </div>
              <div>
                <p style="margin: 0 0 0.5rem; color: var(--landi-text-muted);">Purity Result</p>
                <p style="margin: 0; font-weight: 600; color: var(--landi-success);">${p.metadata?.purity}</p>
              </div>
              <div>
                <p style="margin: 0 0 0.5rem; color: var(--landi-text-muted);">Test Date</p>
                <p style="margin: 0; font-weight: 600;">${p.metadata?.testDate}</p>
              </div>
            </div>
            <hr style="border: none; border-top: 1px solid var(--landi-border); margin: 1.5rem 0;" />
            <div style="display: flex; align-items: center; justify-content: center; height: 150px; background: white; border: 1px dashed var(--landi-border); border-radius: var(--landi-radius); color: var(--landi-text-muted);">
              <div style="text-align: center;">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 32px; height: 32px; margin: 0 auto 0.5rem;">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span>Full PDF Document Available</span>
              </div>
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <p style="font-size: 0.813rem; color: var(--landi-text-muted); margin: 0; max-width: 60%;">This report verifies the chemical identity and purity of the specified lot via third-party testing.</p>
            <div style="display: flex; gap: 1rem;">
              <button class="btn btn-secondary" @click=${() => window.print()}>Print</button>
              <button class="btn btn-primary" @click=${(e: Event) => this.downloadDummyPDF(e, p.name)}>Download PDF</button>
            </div>
          </div>
        </div>
      </div>
      `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-detail': LandiProductDetail;
  }
}
